const baseUrl = import.meta.env.VITE_CRM_API_BASE_URL ?? "/api/v1";

const MAX_REASON_LENGTH = 300;

/**
 * The API answers a business refusal with 409/422 plus an operator-facing Chinese reason. Showing
 * only "CRM Core request failed (409)" makes a deliberate refusal look like a crash, so the reason
 * is carried on the error. Only the bounded reason and code are read from the body; the rest of the
 * payload stays on the server.
 */
export class CoreApiError extends Error {
  constructor(
    readonly status: number,
    readonly code?: string,
    readonly reason?: string,
  ) {
    super(reason ?? `CRM Core request failed (${status})`);
    this.name = "CoreApiError";
  }
}

function errorCode(body: unknown): string | undefined {
  if (!body || typeof body !== "object") return undefined;
  const value = body as { code?: unknown; message?: unknown };
  if (typeof value.code === "string") return value.code;
  if (
    value.message &&
    typeof value.message === "object" &&
    typeof (value.message as { code?: unknown }).code === "string"
  )
    return (value.message as { code: string }).code;
  return undefined;
}

function boundedReason(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.replace(/\s+/g, " ").trim();
  if (!normalized) return undefined;
  return normalized.length > MAX_REASON_LENGTH
    ? `${normalized.slice(0, MAX_REASON_LENGTH)}…`
    : normalized;
}

function errorReason(body: unknown): string | undefined {
  if (!body || typeof body !== "object") return undefined;
  const value = body as { message?: unknown; detail?: unknown };
  if (Array.isArray(value.message)) {
    const joined = value.message
      .map((entry) => (typeof entry === "string" ? entry : ""))
      .filter(Boolean)
      .join("；");
    return boundedReason(joined);
  }
  if (value.message && typeof value.message === "object") {
    const nested = boundedReason(
      (value.message as { message?: unknown }).message,
    );
    if (nested) return nested;
  }
  return boundedReason(value.message) ?? boundedReason(value.detail);
}

function coreApiError(status: number, body: unknown): CoreApiError {
  return new CoreApiError(status, errorCode(body), errorReason(body));
}

export async function coreGet<T>(
  path: string,
  signal?: AbortSignal,
  teamWorkspaceId?: string,
): Promise<T> {
  const response = await fetch(`${baseUrl}${path}`, {
    credentials: "include",
    cache: "no-store",
    signal,
    headers: {
      Accept: "application/json",
      ...(teamWorkspaceId ? { "x-team-workspace-id": teamWorkspaceId } : {}),
    },
  });
  if (!response.ok) {
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      /* bounded category only */
    }
    throw coreApiError(response.status, body);
  }
  return response.json() as Promise<T>;
}

export async function coreDownload(
  path: string,
  signal?: AbortSignal,
): Promise<{ blob: Blob; filename: string }> {
  const response = await fetch(`${baseUrl}${path}`, {
    credentials: "include",
    cache: "no-store",
    signal,
    headers: { Accept: "application/octet-stream" },
  });
  if (!response.ok) {
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      /* bounded category only */
    }
    throw coreApiError(response.status, body);
  }
  const disposition = response.headers.get("content-disposition") ?? "";
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const fallback = disposition.match(/filename="?([^";]+)"?/i)?.[1];
  return {
    blob: await response.blob(),
    filename: encoded
      ? decodeURIComponent(encoded)
      : (fallback ?? "attachment"),
  };
}

export async function coreAnonymousPost<T>(
  path: string,
  body: unknown,
): Promise<T> {
  const response = await fetch(`${baseUrl}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    let value: unknown;
    try {
      value = await response.json();
    } catch {
      /* bounded category only */
    }
    throw coreApiError(response.status, value);
  }
  return response.json() as Promise<T>;
}

export async function coreMutate<T>(
  path: string,
  method: "POST" | "PATCH" | "PUT" | "DELETE",
  body: unknown,
  idempotencyKey = `crm-fe-${crypto.randomUUID()}`,
  teamWorkspaceId?: string,
): Promise<T> {
  const csrf = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith("crm_session_csrf="))
    ?.split("=")[1];
  if (!csrf) throw new CoreApiError(401, "SESSION_EXPIRED");
  const response = await fetch(`${baseUrl}${path}`, {
    method,
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-csrf-token": csrf,
      "x-idempotency-key": idempotencyKey,
      ...(teamWorkspaceId ? { "x-team-workspace-id": teamWorkspaceId } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    let value: unknown;
    try {
      value = await response.json();
    } catch {
      /* no raw error body */
    }
    throw coreApiError(response.status, value);
  }
  return response.json() as Promise<T>;
}

export async function coreUpload<T>(
  path: string,
  body: FormData,
  idempotencyKey = `crm-fe-upload-${crypto.randomUUID()}`,
): Promise<T> {
  const csrf = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith("crm_session_csrf="))
    ?.split("=")[1];
  if (!csrf) throw new CoreApiError(401, "SESSION_EXPIRED");
  const response = await fetch(`${baseUrl}${path}`, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "x-csrf-token": csrf,
      "x-idempotency-key": idempotencyKey,
    },
    body,
  });
  if (!response.ok) {
    let value: unknown;
    try {
      value = await response.json();
    } catch {
      /* no raw error body */
    }
    throw coreApiError(response.status, value);
  }
  return response.json() as Promise<T>;
}
